import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Users, MagnifyingGlass, CaretDown, CaretRight, Calendar, ArrowClockwise, Moon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const DIAS_LETRAS = ["L", "M", "M", "J", "V", "S", "D"];
const DIAS_NOMBRES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const fmtFecha = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

const diasDesdeHoy = (iso) => {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const then = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ms = today.getTime() - then.getTime();
    return Math.max(0, Math.floor(ms / 86400000));
  } catch (_) { return null; }
};

/** Cliente "dormido": tiene promedio de días entre compras (≥2 compras) y la
 * última compra ya duplica ese promedio (o más). Devuelve {esDormido, ratio}. */
const detectarDormido = (cliente) => {
  const prom = Number(cliente.prom_dias_entre_compras);
  const diasUlt = diasDesdeHoy(cliente.ultima_compra);
  if (!Number.isFinite(prom) || prom <= 0 || diasUlt == null) {
    return { esDormido: false, ratio: null };
  }
  const ratio = diasUlt / prom;
  return { esDormido: ratio >= 2, ratio };
};

function GrupoBlock({ grupo, q, soloDormidos }) {
  const [visibleCount, setVisibleCount] = useState(10);
  const [collapsed, setCollapsed] = useState(false);

  const filtered = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    let arr = grupo.clientes;
    if (soloDormidos) arr = arr.filter((c) => detectarDormido(c).esDormido);
    if (!s) return arr;
    return arr.filter((c) => (c.nombre || "").toLowerCase().includes(s));
  }, [grupo.clientes, q, soloDormidos]);

  useEffect(() => { setVisibleCount(10); }, [q, soloDormidos, grupo.clientes.length]);

  const visibleRows = visibleCount >= filtered.length ? filtered : filtered.slice(0, visibleCount);
  const totalCompras30 = filtered.reduce((s, c) => s + (c.cant_compras_30d || 0), 0);
  const activos30 = filtered.filter((c) => (c.cant_compras_30d || 0) > 0).length;
  const dormidosCount = grupo.clientes.filter((c) => detectarDormido(c).esDormido).length;

  if (filtered.length === 0 && (q || soloDormidos)) return null;

  return (
    <div className="border border-neutral-200 rounded-sm bg-white mb-3" data-testid={`frec-grupo-${grupo.nombre}`}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full px-4 py-2 border-b border-neutral-100 bg-neutral-50 flex items-center gap-2 hover:bg-neutral-100"
        data-testid={`frec-grupo-toggle-${grupo.nombre}`}
      >
        {collapsed ? <CaretRight size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        <span className="font-heading font-semibold text-[13px] text-[#6B1F24]">{grupo.nombre}</span>
        <span className="text-[11px] font-mono text-neutral-500">
          {filtered.length} cliente{filtered.length === 1 ? "" : "s"} · {activos30} activo{activos30 === 1 ? "" : "s"} 30d · {totalCompras30} compras
        </span>
        {dormidosCount > 0 && (
          <span
            className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-amber-100 text-amber-900 border border-amber-300"
            title={`${dormidosCount} cliente(s) con última compra que supera el doble de su promedio`}
            data-testid={`frec-grupo-dormidos-count-${grupo.nombre}`}
          >
            <Moon size={10} weight="fill" /> {dormidosCount} dormido{dormidosCount === 1 ? "" : "s"}
          </span>
        )}
      </button>
      {!collapsed && (
        <>
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-neutral-400 text-[12px]">Sin clientes en este grupo.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-neutral-50/70 text-[10px] uppercase font-mono text-neutral-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Cliente</th>
                    <th className="px-3 py-2 text-center w-24">Compras 30d</th>
                    <th className="px-3 py-2 text-center w-28">Cada X días</th>
                    <th className="px-3 py-2 text-center w-52">Días frecuentes</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((c, i) => {
                    const diasUlt = diasDesdeHoy(c.ultima_compra);
                    const activo = (c.cant_compras_30d || 0) > 0;
                    const { esDormido, ratio } = detectarDormido(c);
                    return (
                      <tr key={c.cliente_id} className={`border-t border-neutral-100 ${esDormido ? "bg-amber-50/40 hover:bg-amber-50" : "hover:bg-neutral-50/60"}`}
                          data-testid={`frec-cli-row-${grupo.nombre}-${i}`}>
                        <td className="px-3 py-1.5">
                          <div className="font-medium truncate max-w-[280px] flex items-center gap-1.5" title={c.nombre}>
                            {c.nombre}
                            {esDormido && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-amber-100 text-amber-900 border border-amber-300"
                                title={`Dormido: última compra hace ${diasUlt} días, su promedio es ${Number(c.prom_dias_entre_compras).toFixed(1)}d (×${ratio.toFixed(1)})`}
                                data-testid={`frec-cli-dormido-badge-${grupo.nombre}-${i}`}
                              >
                                <Moon size={8} weight="fill" /> Dormido ×{ratio.toFixed(1)}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] font-mono text-neutral-400">
                            {c.ultima_compra ? (
                              <>Última compra: <span className="text-neutral-600">{fmtFecha(c.ultima_compra)}</span>
                                {diasUlt != null && (
                                  <span className={`ml-1 ${esDormido ? "text-amber-700 font-bold" : diasUlt <= 7 ? "text-emerald-600" : diasUlt <= 30 ? "text-neutral-500" : "text-rose-600"}`}>
                                    · hace {diasUlt} día{diasUlt === 1 ? "" : "s"}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="italic">Sin compras registradas</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-center font-mono font-bold">
                          <span className={activo ? "text-emerald-700" : "text-neutral-400"}>
                            {c.cant_compras_30d || 0}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-center font-mono">
                          {c.prom_dias_entre_compras != null ? (
                            <span>
                              {Number(c.prom_dias_entre_compras).toLocaleString("es-AR", { maximumFractionDigits: 1 })}
                              <span className="text-[10px] text-neutral-400 ml-0.5">d</span>
                            </span>
                          ) : <span className="text-neutral-300">—</span>}
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center justify-center gap-0.5">
                            {DIAS_LETRAS.map((letra, idx) => {
                              const cant = (c.dias_semana_hist || [])[idx] || 0;
                              const isTop = (c.top_dias || []).includes(idx);
                              return (
                                <div
                                  key={idx}
                                  title={`${DIAS_NOMBRES[idx]}: ${cant} compra${cant === 1 ? "" : "s"}`}
                                  className={`w-6 h-6 rounded-sm text-[10px] font-mono font-bold flex items-center justify-center border ${
                                    isTop
                                      ? "bg-[#6B1F24] text-white border-[#6B1F24]"
                                      : cant > 0
                                        ? "bg-neutral-100 text-neutral-700 border-neutral-200"
                                        : "bg-white text-neutral-300 border-neutral-100"
                                  }`}
                                >
                                  {letra}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {filtered.length > 10 && (
            <div className="px-4 py-2 border-t border-neutral-100 bg-neutral-50 flex items-center justify-between gap-2 flex-wrap"
                 data-testid={`frec-grupo-paginacion-${grupo.nombre}`}>
              <div className="text-[10px] font-mono text-neutral-600">
                Mostrando <span className="font-bold text-neutral-800">{visibleRows.length}</span> de{" "}
                <span className="font-bold text-neutral-800">{filtered.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[9px] uppercase font-mono text-neutral-500">Ver</label>
                <select
                  value={visibleCount >= filtered.length ? "all" : String(visibleCount)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setVisibleCount(v === "all" ? filtered.length : parseInt(v, 10));
                  }}
                  className="h-7 text-[11px] font-mono border border-neutral-300 rounded-sm px-1.5 bg-white focus:outline-none focus:border-[#6B1F24]"
                  data-testid={`frec-grupo-page-size-${grupo.nombre}`}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="all">Todos</option>
                </select>
                {visibleCount < filtered.length && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setVisibleCount((v) => Math.min(filtered.length, v + 10))}
                    className="h-7 rounded-sm text-[10px]"
                    data-testid={`frec-grupo-ver-mas-${grupo.nombre}`}
                  >
                    Ver 10 más
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function FrecuenciaClientesCard() {
  const [ventana, setVentana] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [soloDormidos, setSoloDormidos] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/frecuencia-clientes", { params: { ventana_dias: ventana } });
      setData(data);
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }, [ventana]);
  useEffect(() => { cargar(); }, [cargar]);

  const totalClientes = useMemo(
    () => (data?.grupos || []).reduce((s, g) => s + (g.clientes?.length || 0), 0),
    [data]
  );
  const totalDormidos = useMemo(
    () => (data?.grupos || []).reduce(
      (s, g) => s + (g.clientes || []).filter((c) => detectarDormido(c).esDormido).length,
      0
    ),
    [data]
  );

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm" data-testid="dash-frecuencia-clientes">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <Users size={16} weight="bold" className="text-[#6B1F24]" />
            Frecuencia de Compra por Cliente
          </h2>
          <div className="text-[11px] text-[#666] font-mono mt-0.5 flex items-center gap-1">
            <Calendar size={11} weight="duotone" />
            Compras 30d fijas · patrón de días y promedio sobre los últimos{" "}
            <span className="font-bold text-[#6B1F24]">{data?.ventana_dias || ventana}</span> días
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar cliente…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-7 w-56 text-[12px]"
              data-testid="frec-clientes-search"
            />
          </div>
          <label className="text-[10px] uppercase font-mono text-neutral-500">Ventana</label>
          <select
            value={ventana}
            onChange={(e) => setVentana(parseInt(e.target.value, 10))}
            className="h-8 text-[12px] font-mono border border-neutral-300 rounded-sm px-2 bg-white focus:outline-none focus:border-[#6B1F24]"
            data-testid="frec-clientes-ventana"
          >
            <option value={30}>30 días</option>
            <option value={60}>60 días</option>
            <option value={90}>90 días</option>
            <option value={120}>120 días</option>
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={cargar}
            disabled={loading}
            className="h-8 rounded-sm text-[11px]"
            data-testid="frec-clientes-reload"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      {loading && (
        <div className="p-8 text-center text-neutral-500 text-sm">Cargando…</div>
      )}
      {!loading && !data && (
        <div className="p-8 text-center text-rose-400 text-sm">No se pudo cargar el indicador.</div>
      )}
      {!loading && data && totalClientes === 0 && (
        <div className="p-8 text-center text-neutral-400 text-sm">Sin clientes registrados.</div>
      )}
      {!loading && data && totalClientes > 0 && (
        <div className="p-4">
          {totalDormidos > 0 && (
            <div className="mb-3 flex items-center gap-3 flex-wrap px-3 py-2 border border-amber-200 bg-amber-50 rounded-sm"
                 data-testid="frec-alerta-dormidos-banner">
              <Moon size={14} weight="fill" className="text-amber-700" />
              <div className="text-[12px] text-amber-900 flex-1">
                <span className="font-bold">{totalDormidos}</span> cliente{totalDormidos === 1 ? "" : "s"} dormido{totalDormidos === 1 ? "" : "s"} · última compra ≥ 2× el promedio de días entre compras.
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSoloDormidos((v) => !v)}
                className={`h-7 rounded-sm text-[11px] ${soloDormidos ? "bg-amber-700 text-white border-amber-700 hover:bg-amber-800" : "border-amber-300 text-amber-800 hover:bg-amber-100"}`}
                data-testid="frec-alerta-dormidos-toggle"
              >
                {soloDormidos ? "Ver todos" : "Ver sólo dormidos"}
              </Button>
            </div>
          )}
          {(data.grupos || []).map((g) => (
            <GrupoBlock key={g.nombre} grupo={g} q={q} soloDormidos={soloDormidos} />
          ))}
        </div>
      )}
    </div>
  );
}
